import { LoadingOverlay } from "@mantine/core";
import dayjs              from "dayjs";
import relativeTime       from "dayjs/plugin/relativeTime";
import dynamic            from "next/dynamic";
import { useRouter }      from "next/router";
import Script             from "next/script";

import { useGetProStatus }     from "_common/hooks/auth";
import { useGetDeviceType }    from "_common/hooks/global";
import { useRemoveQueryParam } from "_common/hooks/useQueryParams";
import Modal                   from "_components/atoms/modal";
import Render                  from "_components/atoms/render";
import RetryView               from "_components/atoms/retryView";
import WebViewFns              from "_components/pages/creatorPlus/webViewFns";
import MantineThemeProvider    from "_components/templates/mantineDarkProvider";

const PurchaseHistoryModal = dynamic ( () => import ( "_components/pages/creatorPlus/components/purchaseHistoryModal" ) );
const AlreadyProPage       = dynamic ( () => import ( "_components/pages/creatorPlus/components/alreadyProPage" ) );
const LeftHalf             = dynamic ( () => import ( "_components/pages/creatorPlus/components/leftHalf" ) );
const RightHalf            = dynamic ( () => import ( "_components/pages/creatorPlus/components/rightHalf" ) );
const MobileHeader         = dynamic ( () => import ( "_components/pages/creatorPlus/components/mobileHeader" ) );

import useStyles from "./styles";

const getDefaultPack = plusPacks => {

	let pack = plusPacks?.find ( pack => pack.defaultSelected );

	return {
		...pack,
		finalAmount: pack?.discountedAmount || pack?.recurringAmount
	};
};

function component ( {
	from,
	plusPacks,
	isMobile,
	initialOpen,
	userPlan,
	isProServer,
	emotionCache,
	error,
	isWebView,
	hasSubscription
} ) {
	dayjs.extend ( relativeTime );
	const router                    = useRouter ();
	const isProClient               = useGetProStatus ();
	const removeQueryParam          = useRemoveQueryParam ();
	const proModalQuery             = router.query.proModal;
	const purchaseHistoryModalQuery = router.query.purchaseHistory;
	const deviceType                = useGetDeviceType ();
	const isPro                     = from === "ProPage" ? isProServer : isProClient;
	const isAlreadyPlus             = !!router.query.alreadyPlus;
	 const hasHighestSubscription    = false;

	 const defaultPack = getDefaultPack ( plusPacks );

	return (

		<>
			{!isWebView ? (
				<Script
					crossOrigin = "anonymous"
					src         = { `https://securegw.paytm.in/merchantpgpui/checkoutjs/merchants/${ process.env.PAYTM_MERCHANT_ID }.js` }
					type        = "application/javascript"
				/>
			) : null}

			<WebViewFns />

			<MantineThemeProvider emotionCache = { emotionCache }
				type                              = { "dark" }
			>
				{
					deviceType !== "mobile" ? (
						<DesktopView
							defaultPack              = { defaultPack }
							errorPage                = { error }
							hasHighestSubscription   = { hasHighestSubscription }
							hasSubscription          = { hasSubscription }
							isAlreadyPlus            = { isAlreadyPlus }
							isMobile                 = { isMobile }
							isPro                    = { isPro }
							plusPacks                = { plusPacks }
							userPlan                 = { userPlan }
						/>
					) : (
						<MobileView
							defaultPack              = { defaultPack }
							errorPage                = { error }
							hasHighestSubscription   = { hasHighestSubscription }
							hasSubscription          = { hasSubscription }
							isAlreadyPlus            = { isAlreadyPlus }
							isMobile                 = { isMobile }
							isPro                    = { isPro }
							plusPacks                = { plusPacks }
							userPlan                 = { userPlan }
						/>
					)
				}

				<PurchaseHistoryModal
					onClose  = { () => removeQueryParam ( "purchaseHistory" ) }
					opened   = { purchaseHistoryModalQuery }
					userPlan = { userPlan }
				/>

			</MantineThemeProvider>
		</>
	);
}

function DesktopView ( {
	userPlan,
	isMobile,
	hasHighestSubscription,
	isPro,
	plusPacks,
	errorPage,
	defaultPack,
	isAlreadyPlus,
	hasSubscription
} ) {
	const { classes, cx }  = useStyles ( { alreadyPlus: isAlreadyPlus } );
	const { theme }        = useStyles ();
	const removeQueryParam = useRemoveQueryParam ();

	const onRetry = () => {
		window.location.reload ();
	};

	if ( errorPage ) {
		return (
			<div style = { {
				height     : "100vh",
				width      : "100%",
				background : theme.other.paletteNew.darkModeInclinedBlack2
			} }
			>

				<Render condition = { isMobile }>
					<MobileHeader changeColor = { true } />
				</Render>

				<RetryView onClick = { onRetry } />
			</div>
		);
	}

	return (

		<Modal centered
			classNames          = { {
				body    : classes.modalBodyNew,
				content : cx ( classes.modalContentNew, isAlreadyPlus ? classes.proBorder : undefined ),
				root    : classes.modalRootNew,
				inner   : classes.modalInnerNew
			} }
			closeOnClickOutside = { true }
			closeOnEscape       = { false }
			fullWidth           = { true }
			lockScroll          = { true }
			onClose             = { () => removeQueryParam ( "plusModal" ) }
			opened              = { true }
			size                = { "60%" }
			transitionProps     = { isMobile ? {
				transition: "slide-up"
			} : {
				transition: "fade"
			} }
			withCloseButton     = { false }
			zIndex              = { 1200 }
		>
			<div className = { classes.modalInnerWrapper }>

				{isAlreadyPlus ? (
					<AlreadyProPage
						hasHighestSubscription = { hasHighestSubscription }
						hasSubscription        = { hasSubscription }
						isMobile               = { isMobile }
						userPlan               = { userPlan }
					/>
				) : (
					<>
						<LeftHalf
							expiringTime = { userPlan?.endTime }
							isMobile     = { isMobile }
							isPro        = { isPro }
						/>

						<RightHalf
							defaultPack      = { defaultPack }
							hasSubscription  = { hasSubscription }
							isPro            = { isPro }
							plusPacks        = { plusPacks }
						/>
					</>
				)}
			</div>
		</Modal>

	);
}

function MobileView ( {
	userPlan,
	isMobile,
	hasHighestSubscription,
	isPro,
	plusPacks,
	errorPage,
	defaultPack,
	isAlreadyPlus,
	hasSubscription
} ) {

	const { classes, cx } = useStyles ( { alreadyPlus: isAlreadyPlus } );
	const { theme }       = useStyles ();

	const onRetry = () => {
		window.location.reload ();
	};

	if ( errorPage ) {
		return (
			<div style = { {
				height     : "100vh",
				width      : "100%",
				background : theme.other.paletteNew.darkModeInclinedBlack2
			} }
			>

				<Render condition = { isMobile }>
					<MobileHeader changeColor = { true } />
				</Render>

				<RetryView onClick = { onRetry } />
			</div>
		);
	}

	return (
		<div style = { {
			backgroundColor: "#1a1a1a"
		} }
		>

			{isAlreadyPlus ? (
				<AlreadyProPage
					hasHighestSubscription = { hasHighestSubscription }
					hasSubscription        = { hasSubscription }
					isMobile               = { isMobile }
					userPlan               = { userPlan }
				/>
			) : (
				<>
					<LeftHalf
						expiringTime = { userPlan?.endTime }
						isMobile     = { isMobile }
						isPro        = { isPro }
					/>

					<RightHalf
						defaultPack      = { defaultPack }
						hasSubscription  = { hasSubscription }
						isPro            = { isPro }
						plusPacks        = { plusPacks }
					/>
				</>
			)}
		</div>

	);
}

export default component;
